/**
 * Who this plan may be given to — the roster, reduced to the people a plan can actually reach.
 *
 * `GET /customer` returns four kinds of row and two of them are clients you can assign to:
 *
 * · **An active client.** The list.
 * · **An invited roster entry.** Its engagement is open from the moment the trainer names the
 *   person (R2), so a plan or a survey can be prepared before the client answers — the mobile
 *   pickers offer it too (`canAssignWork`). Offered since 23 September 2026.
 * · **An offer to somebody who is not yet a client** (R12). No engagement until its holder
 *   accepts, so every write against that person is refused. Never offered. Which of the two
 *   invited kinds a row is, is `clientRow`'s `openableCustomerId` — one reading of
 *   `hasOpenEngagement` for the roster link and for both pickers.
 * · **A deactivated client.** `status` is `inactive`; they cannot train, and a plan generated for
 *   them would sit unopened. Offered by neither app.
 *
 * `status` arrives LOWER-CASE, unlike every other enum this API emits — `CustomerResponseMapper`
 * does `Status.ToString().ToLower()`. Matched against an explicit value rather than lower-cased on a
 * guess, so a future PascalCase correction upstream empties the list loudly instead of quietly
 * classifying every client as unknown.
 *
 * ── The response carries no ordering at all ──────────────────────────────────────────────────
 * `CustomerRepository.GetAllAsync` has no `OrderBy`, so Postgres may hand back a different order on
 * every call. A picker whose rows move between openings is unusable, so the sort is ours and is
 * done with a collator — "Żurawski" sorts after "Zawadzki" in Polish and before it under a
 * code-point comparison.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { TrainWithMeModulesClientsDtosResponsesGetCustomerResponse as CustomerDto } from '~/shared/api/generated/model';

import { openableCustomerId } from './clientRow';

/** One person the trainer can hand this plan to. */
export interface AssignableClient {
  readonly customerId: number;
  /** Full name, or the e-mail when the client has neither part of a name stored. */
  readonly label: string;
  /** Shown under the name to tell two people with the same name apart. `null` when absent. */
  readonly email: string | null;
}

function trimmedOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Search-normalised: case-folded and stripped of combining marks.
 *
 * Polish is the source language here, so a trainer typing "zurawski" on a keyboard without the
 * diacritic must still find "Żurawski". `NFD` splits the letter from its mark and the range strips
 * the marks; the fold is `toLowerCase` on the already-decomposed string.
 */
function searchable(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/gu, '')
    .toLowerCase();
}

/**
 * The roster as an assignment target list, sorted for `locale`.
 *
 * `locale` rather than a hardcoded `'pl'`: the app runs in two languages and a collator built for
 * the wrong one puts the diacritics back in the wrong place.
 */
export function assignableClients(
  customers: readonly CustomerDto[] | null | undefined,
  locale: string,
): AssignableClient[] {
  const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });

  const clients: AssignableClient[] = [];
  for (const dto of customers ?? []) {
    const customerId = openableCustomerId(dto);
    if (customerId === null || customerId <= 0) continue;
    if (dto.status !== 'active' && dto.status !== 'invited') continue;

    const email = trimmedOrNull(dto.email);
    // The display name comes off the wire as of the 25 August 2026 contract, already carrying the
    // e-mail fallback this module used to apply itself: the backend composes the name from the
    // parts and answers with the address when neither is set.
    const label = trimmedOrNull(dto.person?.fullName);
    // A row with neither a name nor an e-mail cannot be told apart from the next one, so it is not
    // offered. That is now a row whose composed name came back empty — the backend has already
    // fallen back to the address and no further.
    if (label === null) continue;

    clients.push({ customerId, label, email });
  }

  return clients.sort((a, b) => collator.compare(a.label, b.label));
}

/** The list narrowed by the trainer's query. A blank query returns everything, unfiltered. */
export function matchingClients(
  clients: readonly AssignableClient[],
  query: string,
): AssignableClient[] {
  const needle = searchable(query.trim());
  if (needle === '') return [...clients];
  return clients.filter(
    (client) =>
      searchable(client.label).includes(needle) ||
      (client.email !== null && searchable(client.email).includes(needle)),
  );
}
